<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-4">
      <div>
        <div style="font-size: 18px; font-weight: 700">{{ $t('taxDialog') }}</div>
      </div>
    </div>

    <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mb-4">{{ loadError }}</v-alert>

    <v-skeleton-loader v-if="loading" type="card, card" />

    <v-expansion-panels v-else v-model="openPanels" multiple variant="accordion">
      <v-expansion-panel v-for="dialog in dialogs" :key="dialog.dialogTypeCode" :value="dialog.dialogTypeCode">
        <v-expansion-panel-title>
          <div class="d-flex align-center ga-3">
            <v-avatar size="32" rounded="lg" color="grey-lighten-3">
              <v-img v-if="safeIconUrl(dialog.iconUrl)" :src="safeIconUrl(dialog.iconUrl)" />
              <v-icon v-else size="18" color="grey">mdi-message-alert-outline</v-icon>
            </v-avatar>
            <div>
              <div style="font-weight: 600">{{ dialogLabel(dialog.dialogTypeCode) }}</div>
              <div class="text-caption text-medium-emphasis">{{ dialog.dialogTypeCode }}</div>
            </div>
          </div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <!-- Icon -->
          <div class="td-group-label mb-2">{{ $t('taxDialogIcon') }}</div>
          <div class="d-flex align-center ga-3 mb-6">
            <v-avatar size="56" rounded="lg" color="grey-lighten-3">
              <v-img v-if="safeIconUrl(dialog.iconUrl)" :src="safeIconUrl(dialog.iconUrl)" />
              <v-icon v-else color="grey">mdi-image-outline</v-icon>
            </v-avatar>
            <v-btn variant="outlined" size="small" color="#4E3982" :disabled="!canEdit || uploadingType === dialog.dialogTypeCode" :loading="uploadingType === dialog.dialogTypeCode" @click="triggerIconUpload(dialog.dialogTypeCode)">
              <v-icon start size="16">mdi-upload</v-icon>{{ $t('taxDialogUploadIcon') }}
            </v-btn>
            <v-btn v-if="dialog.iconUrl" variant="text" size="small" color="error" :disabled="!canEdit" @click="dialog.iconUrl = null">
              {{ $t('taxDialogRemoveIcon') }}
            </v-btn>
            <span class="text-caption text-medium-emphasis">{{ $t('taxDialogIconHint') }}</span>
            <v-switch
              inset
              class="ma-0 ml-2 flex-grow-0"
              v-model="dialog.useIcon"
              :label="$t('taxDialogUseIcon')"
              color="#4E3982"
              hide-details
              density="compact"
              :disabled="!canEdit"
            ></v-switch>
          </div>

          <!-- Title — all three languages side by side -->
          <div class="td-group-label mb-2">{{ $t('taxDialogTitle') }}</div>
          <v-row class="mb-4">
            <v-col v-for="lang in languages" :key="lang.key" cols="12" md="4">
              <div class="td-lang-label mb-2">{{ lang.label }}</div>
              <input v-model="dialog['title' + lang.key]" :placeholder="$t('taxDialogTitle')" class="custom-input" maxlength="200" :disabled="!canEdit" />
            </v-col>
          </v-row>

          <!-- Subtitle -->
          <div class="td-group-label mb-2">{{ $t('taxDialogSubtitle') }}</div>
          <v-row class="mb-4">
            <v-col v-for="lang in languages" :key="lang.key" cols="12" md="4">
              <div class="td-lang-label mb-2">{{ lang.label }}</div>
              <textarea
                v-model="dialog['subtitleHtml' + lang.key]"
                class="td-subtitle-input"
                rows="4"
                maxlength="2000"
                :disabled="!canEdit"
                :aria-label="`${$t('taxDialogSubtitle')} (${lang.label})`"
              ></textarea>
            </v-col>
          </v-row>

          <!-- Confirm button -->
          <div class="td-group-label mb-2">{{ $t('taxDialogButtonConfirm') }}</div>
          <v-row class="mb-4">
            <v-col v-for="lang in languages" :key="lang.key" cols="12" md="4">
              <div class="td-lang-label mb-2">{{ lang.label }}</div>
              <input v-model="dialog['confirmButtonText' + lang.key]" :placeholder="$t('taxDialogButtonConfirm')" class="custom-input" maxlength="100" :disabled="!canEdit" />
            </v-col>
          </v-row>

          <!-- Cancel button -->
          <div class="td-group-label mb-2">{{ $t('taxDialogButtonCancel') }}</div>
          <v-row>
            <v-col v-for="lang in languages" :key="lang.key" cols="12" md="4">
              <div class="td-lang-label mb-2">{{ lang.label }}</div>
              <input v-model="dialog['cancelButtonText' + lang.key]" :placeholder="$t('taxDialogButtonCancel')" class="custom-input" maxlength="100" :disabled="!canEdit" />
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <input ref="iconInput" type="file" accept="image/*" style="display: none" @change="handleIconUpload" />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">{{ snackbar.text }}</v-snackbar>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';

// Fixed dialog types — the mobile SDK binds to these codes, so the list is hardcoded here
// and mirrors TaxDialogTypes on the backend. Admins can edit content but not add/remove types.
const DIALOG_TYPES = [
  { code: 'TaxExit', labelKey: 'taxDialogTypeExit' },
  { code: 'TaxUnavailable', labelKey: 'taxDialogTypeUnavailable' },
  { code: 'TaxUnavailableNotWhitelisted', labelKey: 'taxDialogTypeUnavailableNotWhitelisted' },
  { code: 'TaxCancelAgent', labelKey: 'taxDialogTypeCancelAgent' },
  { code: 'TaxConsentRevoke', labelKey: 'taxDialogTypeConsentRevoke' },
  { code: 'TaxConfirmAgent', labelKey: 'taxDialogTypeConfirmAgent' },
  { code: 'MiniContactAgent', labelKey: 'taxDialogTypeMiniContactAgent' },
];

// Subtitle is stored as HTML. The editor is a plain textarea, but the value is still
// sanitized before it is persisted — the mobile SDK renders it, and a hand-typed or
// pasted tag must never reach the app unchecked.
const ALLOWED_TAGS = ['strong', 'b', 'em', 'i', 'span'];
const ALLOWED_ATTR = ['style'];

export default {
  name: 'TaxDialogTab',
  props: {
    mobileConfigurationId: {
      type: Number,
      default: 0,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      loadError: '',
      dialogs: [],
      openPanels: [],
      uploadingType: '',
      pendingIconType: '',
      // Every field renders one column per language at once — no tab to switch.
      languages: [
        { key: 'Th', label: 'In Thai' },
        { key: 'En', label: 'In English' },
        { key: 'Zh', label: 'In Chinese' },
      ],
      snackbar: { show: false, text: '', color: 'success' },
    };
  },
  watch: {
    mobileConfigurationId: {
      immediate: true,
      handler(value) {
        if (value) this.load();
      },
    },
  },
  methods: {
    dialogLabel(code) {
      const found = DIALOG_TYPES.find((t) => t.code === code);
      return found ? this.$t(found.labelKey) : code;
    },

    isApiSuccess(response) {
      return !!response && (response.Status === 'Successful' || response.status === 0);
    },

    load() {
      if (!this.mobileConfigurationId) return;
      this.loading = true;
      this.loadError = '';

      this.$call.GetTaxDialogs(
        this.mobileConfigurationId,
        (response) => {
          this.loading = false;
          if (response && response.status === 0 && Array.isArray(response.data)) {
            // Coerce useIcon so a row saved before the toggle existed still shows as on.
            this.dialogs = response.data.map((d) => ({ ...d, useIcon: d.useIcon !== false }));
            // All panels open by default — there are only ever a handful, so there's no
            // scroll/clutter cost to showing everything up front.
            this.openPanels = this.dialogs.map((d) => d.dialogTypeCode);
          } else {
            this.loadError = (response && response.message) || 'Failed to load tax dialogs';
          }
        },
        (error) => {
          this.loading = false;
          this.loadError = error?.message || 'Failed to load tax dialogs';
        }
      );
    },

    /**
     * Persist every dialog. Called by the page's main Save button — this tab has no
     * save control of its own. Each type is an upsert, so saving them all is safe.
     */
    async saveTaxDialogs(configId) {
      const targetId = configId || this.mobileConfigurationId;
      if (!this.canEdit || !targetId || this.dialogs.length === 0) return;

      for (const dialog of this.dialogs) {
        await this.saveTaxDialog(targetId, dialog);
      }
    },

    async saveTaxDialog(configId, dialog) {
      // Sanitize before persisting: the stored HTML is what the mobile SDK renders, so the
      // saved value must be clean regardless of what was typed into the textarea.
      const request = {
        dialogTypeCode: dialog.dialogTypeCode,
        iconUrl: dialog.iconUrl || null,
        useIcon: dialog.useIcon !== false,
        titleTh: dialog.titleTh || '',
        titleEn: dialog.titleEn || '',
        titleZh: dialog.titleZh || '',
        subtitleHtmlTh: this.sanitizeHtml(dialog.subtitleHtmlTh),
        subtitleHtmlEn: this.sanitizeHtml(dialog.subtitleHtmlEn),
        subtitleHtmlZh: this.sanitizeHtml(dialog.subtitleHtmlZh),
        confirmButtonTextTh: dialog.confirmButtonTextTh || '',
        confirmButtonTextEn: dialog.confirmButtonTextEn || '',
        confirmButtonTextZh: dialog.confirmButtonTextZh || '',
        cancelButtonTextTh: dialog.cancelButtonTextTh || '',
        cancelButtonTextEn: dialog.cancelButtonTextEn || '',
        cancelButtonTextZh: dialog.cancelButtonTextZh || '',
      };

      const response = await new Promise((resolve, reject) => {
        this.$call.UpdateTaxDialog(
          configId,
          request,
          (res) => resolve(res),
          (error) => {
            console.error('Error saving tax dialog:', error);
            reject(error);
          }
        );
      });

      if (!this.isApiSuccess(response)) {
        throw new Error(response?.Message || response?.message || `Failed to save ${dialog.dialogTypeCode}.`);
      }

      if (response.data || response.Data) {
        Object.assign(dialog, response.data || response.Data);
      }
    },

    triggerIconUpload(dialogTypeCode) {
      this.pendingIconType = dialogTypeCode;
      this.$refs.iconInput.value = '';
      this.$refs.iconInput.click();
    },

    handleIconUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (!file || !this.pendingIconType) return;

      if (file.size > 2 * 1024 * 1024) {
        this.notify(this.$t('taxDialogIconTooLarge'), 'error');
        return;
      }

      const dialogTypeCode = this.pendingIconType;
      const dialog = this.dialogs.find((d) => d.dialogTypeCode === dialogTypeCode);
      if (!dialog) return;

      this.uploadingType = dialogTypeCode;
      const formData = new FormData();
      formData.append('entity', file, file.name);

      // Upload returns the blob URL; it is persisted by the page's main Save, matching the
      // Genie character icon flow.
      this.$call.UploadTaxDialogIcon(
        { entity: formData, USER_CHANNEL: this.$root.User_Id || 'mobile_config', dialogTypeCode },
        (response) => {
          this.uploadingType = '';
          if (response && response.status === 0 && response.data) {
            dialog.iconUrl = response.data;
            this.notify(this.$t('taxDialogIconUploaded'), 'success');
          } else {
            this.notify((response && response.message) || 'Upload failed', 'error');
          }
        },
        (error) => {
          this.uploadingType = '';
          this.notify(error?.message || 'Upload failed', 'error');
        }
      );
    },

    sanitizeHtml(raw) {
      return DOMPurify.sanitize(String(raw || '').trim(), {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
      });
    },

    // Only render icons served over http(s) or a relative path — blocks javascript: URIs.
    safeIconUrl(raw) {
      const value = String(raw || '').trim();
      if (!value) return '';
      return /^(https?:\/\/|\/)/i.test(value) ? value : '';
    },

    notify(text, color) {
      this.snackbar = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
.td-group-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.td-lang-label {
  font-size: 16px;
  font-weight: 500;
}

.custom-input {
  width: 100%;
  height: 45px;
  padding: 8px 16px;
  border: 1px solid #bec2ca;
  border-radius: 16px;
  background-color: white;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;
}

.custom-input:focus {
  border-color: #643990;
  outline: none;
}

.custom-input::placeholder {
  color: #9e9e9e;
}

.custom-input:disabled {
  background-color: #f5f5f5;
  color: #9e9e9e;
}

.td-subtitle-input {
  width: 100%;
  border: 1px solid #bec2ca;
  border-radius: 16px;
  padding: 10px 16px;
  background-color: white;
  font-size: 14px;
  line-height: 1.5;
  outline: none;
  resize: vertical;
  transition: border-color 0.2s ease;
}

.td-subtitle-input:focus {
  border-color: #643990;
  outline: none;
}

.td-subtitle-input:disabled {
  background-color: #f5f5f5;
  color: #9e9e9e;
}
</style>
